import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  BillingCodeSource,
  WorkingBillingClaimLineItem,
  WorkingClaimLineItem,
} from '@features/encounters';
import {
  getUnrepresentedNonConformingLines,
  stripCodeSystem,
} from '@features/encounters/utils/billingClaim';
import { Add, AutoAwesome, Close, InfoOutlined, Remove } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  type SxProps,
  TextField,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isBillableCodeShape, normalizeToCodeId } from '@utils/billingCode';
import { normalizeCptCode } from '@utils/modalityPlaceOfService';

/**
 * CPT Code option with metadata
 */
export interface CPTCodeOption {
  cpt: string;
  description: string;
  category:
    | 'Evaluation'
    | 'Psychotherapy'
    | 'Add-on'
    | 'Crisis'
    | 'Neuromodulation'
    | 'Medication Management'
    | 'Audio-Video Telehealth'
    | 'Audio-Only Telehealth'
    | 'Other';
  /** Flags synthetic "Add custom code" entries appended by filterOptions */
  __custom?: boolean;
}

export type CPTCodePickerLineItem = WorkingBillingClaimLineItem;

const setRefValue = <T,>(ref: React.Ref<T> | undefined, value: T | null) => {
  if (!ref) return;

  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  ref.current = value;
};

/**
 * Common CPT codes for psychiatric/behavioral health encounters
 * Ordered by category: Medication Management, Evaluation, Psychotherapy, Crisis, Other, Add-on
 */
export const CPT_CODES: CPTCodeOption[] = [
  // Medication Management (E/M codes)
  {
    cpt: '99212',
    description: 'E/M established patient, low complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99213',
    description: 'E/M established patient, moderate complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99214',
    description: 'E/M established patient, high complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99215',
    description: 'E/M established patient, highest complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99203',
    description: 'E/M new patient, moderate complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99204',
    description: 'E/M new patient, high complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99205',
    description: 'E/M new patient, highest complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99211',
    description: 'E/M established patient, minimal',
    category: 'Medication Management',
  },
  {
    cpt: 'G2082',
    description: 'Spravato treatment and observation, 56 mg',
    category: 'Medication Management',
  },
  {
    cpt: 'G2083',
    description: 'Spravato treatment and observation, 84 mg',
    category: 'Medication Management',
  },

  // Audio-Video Telehealth E/M (AMA 2025)
  {
    cpt: '98004',
    description: 'Audio-video E/M established patient, straightforward (10+ min)',
    category: 'Audio-Video Telehealth',
  },
  {
    cpt: '98005',
    description: 'Audio-video E/M established patient, low complexity (20+ min)',
    category: 'Audio-Video Telehealth',
  },
  {
    cpt: '98006',
    description: 'Audio-video E/M established patient, moderate complexity (30+ min)',
    category: 'Audio-Video Telehealth',
  },
  {
    cpt: '98007',
    description: 'Audio-video E/M established patient, high complexity (40+ min)',
    category: 'Audio-Video Telehealth',
  },
  {
    cpt: '98000',
    description: 'Audio-video E/M new patient, straightforward (15+ min)',
    category: 'Audio-Video Telehealth',
  },
  {
    cpt: '98001',
    description: 'Audio-video E/M new patient, low complexity (30+ min)',
    category: 'Audio-Video Telehealth',
  },
  {
    cpt: '98002',
    description: 'Audio-video E/M new patient, moderate complexity (45+ min)',
    category: 'Audio-Video Telehealth',
  },
  {
    cpt: '98003',
    description: 'Audio-video E/M new patient, high complexity (60+ min)',
    category: 'Audio-Video Telehealth',
  },

  // Audio-Only Telehealth E/M (AMA 2025)
  {
    cpt: '98012',
    description: 'Audio-only E/M established patient, straightforward (10+ min)',
    category: 'Audio-Only Telehealth',
  },
  {
    cpt: '98013',
    description: 'Audio-only E/M established patient, low complexity (20+ min)',
    category: 'Audio-Only Telehealth',
  },
  {
    cpt: '98014',
    description: 'Audio-only E/M established patient, moderate complexity (30+ min)',
    category: 'Audio-Only Telehealth',
  },
  {
    cpt: '98015',
    description: 'Audio-only E/M established patient, high complexity (40+ min)',
    category: 'Audio-Only Telehealth',
  },
  {
    cpt: '98008',
    description: 'Audio-only E/M new patient, straightforward (15+ min)',
    category: 'Audio-Only Telehealth',
  },
  {
    cpt: '98009',
    description: 'Audio-only E/M new patient, low complexity (30+ min)',
    category: 'Audio-Only Telehealth',
  },
  {
    cpt: '98010',
    description: 'Audio-only E/M new patient, moderate complexity (45+ min)',
    category: 'Audio-Only Telehealth',
  },
  {
    cpt: '98011',
    description: 'Audio-only E/M new patient, high complexity (60+ min)',
    category: 'Audio-Only Telehealth',
  },

  // Evaluation
  {
    cpt: '90791',
    description: 'Psychiatric diagnostic evaluation (no medical services)',
    category: 'Evaluation',
  },
  {
    cpt: '90792',
    description: 'Psychiatric diagnostic evaluation with medical services',
    category: 'Evaluation',
  },

  // Psychotherapy
  {
    cpt: '90832',
    description: 'Psychotherapy, 30 minutes (individual)',
    category: 'Psychotherapy',
  },
  {
    cpt: '90834',
    description: 'Psychotherapy, 45 minutes (individual)',
    category: 'Psychotherapy',
  },
  {
    cpt: '90837',
    description: 'Psychotherapy, 60 minutes (individual)',
    category: 'Psychotherapy',
  },
  {
    cpt: '90846',
    description: 'Family or couples psychotherapy, without patient present',
    category: 'Psychotherapy',
  },
  {
    cpt: '90847',
    description: 'Family or couples psychotherapy, with patient present',
    category: 'Psychotherapy',
  },
  { cpt: '90853', description: 'Group psychotherapy', category: 'Psychotherapy' },
  { cpt: '90845', description: 'Psychoanalysis', category: 'Psychotherapy' },

  // Transcranial magnetic stimulation (TMS)
  {
    cpt: '90867',
    description: 'TMS initial treatment with mapping and motor-threshold determination',
    category: 'Neuromodulation',
  },
  {
    cpt: '90868',
    description: 'TMS treatment session',
    category: 'Neuromodulation',
  },
  {
    cpt: '90869',
    description: 'TMS motor-threshold re-determination with treatment',
    category: 'Neuromodulation',
  },

  // Crisis
  { cpt: '90839', description: 'Psychotherapy for crisis, first 60 minutes', category: 'Crisis' },

  // Other
  { cpt: '90865', description: 'Narcosynthesis', category: 'Other' },
  {
    cpt: '96127',
    description: 'Brief emotional/behavioral assessment (e.g., PHQ-9, GAD-7)',
    category: 'Other',
  },

  // Add-on codes
  { cpt: '90833', description: 'Psychotherapy, 30 minutes (add-on with E/M)', category: 'Add-on' },
  { cpt: '90836', description: 'Psychotherapy, 45 minutes (add-on with E/M)', category: 'Add-on' },
  { cpt: '90838', description: 'Psychotherapy, 60 minutes (add-on with E/M)', category: 'Add-on' },
  {
    cpt: '90840',
    description: 'Psychotherapy for crisis, additional 30 minutes (add-on)',
    category: 'Add-on',
  },
  { cpt: '90785', description: 'Interactive complexity (add-on)', category: 'Add-on' },
  { cpt: '90882', description: 'Environmental intervention (add-on)', category: 'Add-on' },
  {
    cpt: '90887',
    description:
      'Interpretation or explanation of psychiatric/medical data to family or caregivers (add-on)',
    category: 'Add-on',
  },
  {
    cpt: 'G2212',
    description:
      'Prolonged outpatient E/M service, each additional 15 minutes (HCPCS add-on; Medicare/Medicaid alternative to 99417)',
    category: 'Add-on',
  },
  {
    cpt: '99417',
    description:
      'Prolonged outpatient/office E/M service, each additional 15 minutes (CPT add-on; commercial counterpart of G2212)',
    category: 'Add-on',
  },
  {
    cpt: '99418',
    description:
      'Prolonged inpatient/observation E/M service, each additional 15 minutes (CPT add-on)',
    category: 'Add-on',
  },
];

/**
 * Common billing modifiers for psychiatric/behavioral health
 */
export interface ModifierOption {
  code: string;
  description: string;
}

export const COMMON_MODIFIERS: ModifierOption[] = [
  { code: '25', description: 'Significant, separately identifiable E/M service' },
  { code: '95', description: 'Synchronous telemedicine service' },
  { code: 'GT', description: 'Via interactive audio/video telecomm systems' },
  { code: '59', description: 'Distinct procedural service' },
  { code: '76', description: 'Repeat procedure by same physician' },
  { code: '77', description: 'Repeat procedure by another physician' },
  { code: 'HO', description: 'Masters degree level' },
  { code: 'XE', description: 'Separate encounter' },
  { code: 'XS', description: 'Separate structure' },
  { code: 'XP', description: 'Separate practitioner' },
  { code: 'XU', description: 'Unusual non-overlapping service' },
  { code: '93', description: 'Synchronous telemedicine service via audio-only (no video)' },
  { code: 'HE', description: 'Mental health program service' },
  { code: 'HN', description: 'Bachelors degree level' },
  { code: 'HP', description: 'Doctoral degree level' },
  { code: 'HQ', description: 'Group setting' },
  { code: 'HR', description: 'Family/couple therapy, client present' },
  { code: 'HS', description: 'Family/couple therapy, client not present' },
  { code: 'AJ', description: 'Clinical social worker only (not for LCSW, LPC, or LMFT)' },
  { code: 'SA', description: 'Nurse practitioner service in collaboration with a physician' },
  { code: 'SH', description: 'Second concurrently administered infusion therapy' },
  { code: 'SJ', description: 'Third or more concurrently administered infusion therapy' },
  { code: 'JW', description: 'Drug amount discarded/not administered to patient' },
  { code: 'JZ', description: 'Zero drug wasted (single-dose drug)' },
];

const MODIFIER_CODES = COMMON_MODIFIERS.map(m => m.code);

/**
 * Clean CPT codes while preserving duplicate claim lines. Modifier suffixes entered in the
 * base-code input are stripped here; dedicated modifier controls preserve rich line-item data.
 * Use normalizeCptCodes for appointment/session defaults where duplicate code entries should
 * collapse to one default.
 */
export const cleanCptCodes = (codes: string[]): string[] => {
  return codes.map(normalizeCptCode).filter((code): code is string => Boolean(code));
};

export interface CPTCodeMultiSelectProps {
  disabled?: boolean;
  helperText?: React.ReactNode;
  label?: string;
  onChange: (codes: string[]) => void;
  required?: boolean;
  value?: string[];
}

/**
 * Chip-based multi-select for form fields whose API contract is a unique
 * string array rather than ordered claim lines.
 */
export const CPTCodeMultiSelect: React.FC<CPTCodeMultiSelectProps> = ({
  disabled = false,
  helperText,
  label = 'CPT codes',
  onChange,
  required = false,
  value = [],
}) => {
  const selectedCodes = useMemo(
    () => Array.from(new Set(cleanCptCodes(value).filter(isBillableCodeShape))),
    [value],
  );
  const selectedOptions = useMemo<CPTCodeOption[]>(
    () =>
      selectedCodes.map(
        code =>
          CPT_CODES.find(option => option.cpt === code) ?? {
            cpt: code,
            description: 'Custom billing code',
            category: 'Other',
          },
      ),
    [selectedCodes],
  );

  return (
    <Autocomplete<CPTCodeOption, true, false, true>
      multiple
      freeSolo
      disableCloseOnSelect
      filterSelectedOptions
      disabled={disabled}
      options={CPT_CODES}
      value={selectedOptions}
      groupBy={option => option.category}
      getOptionLabel={option =>
        typeof option === 'string' ? option : `${option.cpt} - ${option.description}`
      }
      isOptionEqualToValue={(option, selected) =>
        typeof selected !== 'string' && option.cpt === selected.cpt
      }
      onChange={(_event, options) => {
        const codes = options.flatMap(option => {
          const normalized = normalizeCptCode(typeof option === 'string' ? option : option.cpt);
          return normalized && isBillableCodeShape(normalized) ? [normalized] : [];
        });
        onChange(Array.from(new Set(codes)));
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={key} {...optionProps}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {option.cpt}
              </Typography>
              <Typography color="text.secondary" variant="caption">
                {option.description}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderValue={(selected, getItemProps) =>
        selected.map((option, index) => {
          const code = typeof option === 'string' ? option : option.cpt;
          const { key, ...chipProps } = getItemProps({ index });
          return (
            <Chip
              key={key}
              label={code}
              size="small"
              deleteIcon={<Close aria-label={`Remove CPT code ${code}`} />}
              {...chipProps}
            />
          );
        })
      }
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          placeholder={selectedCodes.length === 0 ? 'Search CPT codes' : undefined}
          required={required && selectedCodes.length === 0}
        />
      )}
    />
  );
};

export interface CPTCodePickerProps {
  /** Array of selected CPT codes */
  value?: string[];
  /** Callback when codes change */
  onChange: (codes: string[]) => void;
  /** Optional callback when Enter key is pressed in any field */
  onEnter?: () => void;
  /** Label for each CPT input. Pass null when an outer field label is already visible. */
  inputLabel?: string | null;
  /**
   * Binds the code input to a caption the caller renders itself, for surfaces
   * whose form grammar puts labels above fields rather than floating in them.
   * Without it, the only way to label the picker externally is to wrap it in a
   * `<label>` — and MUI's Autocomplete carries a `notranslate` span holding a
   * zero-width space, which lands in the accessible name ("CPT\u200b").
   * With more than one code slot, later slots take `${inputId}-${index}` so
   * the ids stay unique.
   */
  inputId?: string;
  /** Optional style overrides for each CPT input TextField. */
  textFieldSx?: SxProps<Theme>;
  /** Optional style overrides for the autocomplete dropdown popper. */
  popperSx?: SxProps<Theme>;
  /** Whether the inputs are disabled */
  disabled?: boolean;
  /**
   * Whether to show the header with description and info tooltip
   * @default true
   */
  showHeader?: boolean;
  /**
   * Size of the TextField inputs
   * @default 'small'
   */
  size?: 'small' | 'medium';
  /** AI-suggested CPT codes to display with a "Use Suggested Codes" button */
  suggestedCodes?: string[];
  /** Callback when user clicks "Use Suggested Codes" button */
  onUseSuggestedCodes?: () => void;
  /**
   * Rich line items from working_billing_claim.
   * When provided, display codes are derived from these items (stripping code system prefix).
   */
  lineItems?: CPTCodePickerLineItem[];
  /** Callback when line items change. Fires alongside onChange with rich metadata preserved. */
  onLineItemsChange?: (items: WorkingClaimLineItem[]) => void;
  /**
   * Whether to show inline modifier editing below each CPT code field.
   * @default false
   */
  showModifiers?: boolean;
  /**
   * Whether to show the helper text below the inputs
   * @default true
   */
  showHelperText?: boolean;
  /**
   * Maximum number of code rows.
   *
   * Omitted means unlimited, which is what every encounter-level consumer
   * wants — a visit's whole code set. `1` makes this a SINGLE-code field:
   * the "Add code" button and the per-row remove control disappear, and
   * picking a new option replaces the current one in place. That matters
   * where the caller's field holds exactly one code (a CMS-1500 service
   * line's box 24D), because a second row would have nowhere to be written
   * and committing only the first would silently drop it.
   *
   * Mirrors `ICD10CodePicker`'s `maxCodes`, which already has this same
   * single-value replace-in-place mode.
   */
  maxCodes?: number;
  /**
   * Claim-level ICD-10 diagnosis codes available for per-line linking.
   * When provided, the expansion area shows toggleable diagnosis chips.
   */
  claimDiagnosisCodes?: string[];
}

const CPT_INFO_TOOLTIP =
  "We'll use these CPT codes when writing the medical decision making. If you don't select a code, we'll assume one based on the content and complexity of the encounter.";

/**
 * CPT Code Picker with dynamic rows and autocomplete.
 *
 * Renders a vertical list of CPT code rows. Users can add/remove rows
 * to select any number of codes. Each code has an optional expansion
 * area for modifiers, units, and diagnosis linking.
 */
export const CPTCodePicker: React.FC<CPTCodePickerProps> = ({
  value = [],
  onChange,
  onEnter,
  inputLabel = 'CPT code',
  inputId,
  textFieldSx,
  popperSx,
  disabled = false,
  showHeader = true,
  size = 'small',
  suggestedCodes,
  onUseSuggestedCodes,
  lineItems,
  onLineItemsChange,
  showModifiers = false,
  showHelperText = true,
  maxCodes,
  claimDiagnosisCodes = [],
}) => {
  const theme = useTheme();
  const rowCap = maxCodes ?? Infinity;
  const isSingleSelect = maxCodes === 1;

  // When lineItems are provided, derive display codes from them
  const derivedValue = useMemo(() => {
    if (lineItems) {
      return lineItems.map(li => (li.code_id ? stripCodeSystem(li.code_id) : ''));
    }
    return value;
  }, [lineItems, value]);

  // Track how many rows to show (at least 1, grows when codes are added, never past the cap)
  const [rowCount, setRowCount] = useState<number>(() =>
    Math.min(Math.max(1, derivedValue.length), rowCap),
  );

  // Stable IDs for each row — prevents React key reuse bugs on removal
  const rowIdsRef = useRef<string[]>(
    Array.from({ length: Math.max(1, derivedValue.length) }, () => crypto.randomUUID()),
  );
  // Ensure rowIds array stays in sync with rowCount (pad if needed)
  while (rowIdsRef.current.length < rowCount) {
    rowIdsRef.current.push(crypto.randomUUID());
  }

  // Sync rowCount when external value changes (e.g. "Use Suggested Codes")
  useEffect(() => {
    setRowCount(prev => Math.min(Math.max(prev, derivedValue.length, 1), rowCap));
  }, [derivedValue, rowCap]);

  // Build the internal codes array, padded to rowCount
  const codes = useMemo(() => {
    const c = derivedValue.map(code => normalizeCptCode(code) ?? '');
    while (c.length < rowCount) c.push('');
    return c;
  }, [derivedValue, rowCount]);

  // Helper: build line items from bare codes, preserving metadata from existing items.
  // Non-conforming legacy lines (e.g. `CPT:CASH`) can't be represented as bare
  // codes — this picker renders them as empty slots — so they are re-appended
  // unchanged rather than silently dropped: omitting them from a full-replace
  // PUT would permanently destroy the persisted line (GDN-2658). The outbound
  // write gate blocks the PUT with an actionable message instead.
  const buildLineItems = useCallback(
    (bareCodes: string[]): WorkingClaimLineItem[] => {
      const items: WorkingClaimLineItem[] = bareCodes.filter(Boolean).map(bareCode => {
        const existing = lineItems?.find(
          li => li.code_id && stripCodeSystem(li.code_id) === bareCode,
        );
        if (existing) {
          return {
            code_id: normalizeToCodeId(existing.code_id ?? bareCode),
            source: existing.source ?? 'provider_selected',
            modifiers: existing.modifiers,
            modifier_sources: existing.modifier_sources,
            units: existing.units ?? undefined,
            charge_cents: existing.charge_cents ?? undefined,
            diagnosis_codes: existing.diagnosis_codes,
          };
        }
        return {
          code_id: normalizeToCodeId(bareCode),
          source: 'provider_selected' as const,
        };
      });

      return [...items, ...getUnrepresentedNonConformingLines(lineItems, items)];
    },
    [lineItems],
  );

  // Track which code slot has its details area expanded (only one at a time)
  const [expandedSlot, setExpandedSlot] = useState<number | null>(null);

  const toggleSlotExpanded = useCallback((index: number) => {
    setExpandedSlot(prev => (prev === index ? null : index));
  }, []);

  // Array-based local input state for controlled typing
  const [inputValues, setInputValues] = useState<string[]>(() => codes.map(c => c || ''));

  const setInputAtIndex = useCallback((index: number, val: string) => {
    setInputValues(prev => {
      const next = [...prev];
      next[index] = val;
      return next;
    });
  }, []);

  // Sync local input state when committed parent value changes (e.g. "Use Suggested Codes").
  // Only overwrite slots whose committed code actually changed — preserve in-progress typing.
  useEffect(() => {
    setInputValues(prev => {
      const next = derivedValue.map((c, i) => {
        const committed = normalizeCptCode(c) ?? '';
        // If this slot exists in prev and committed code hasn't changed, keep user's typing
        if (i < prev.length && prev[i] === committed) return prev[i];
        // If committed code changed (or is new from external), update to committed value
        if (committed) return committed;
        // For slots that already exist in prev, preserve whatever the user typed
        if (i < prev.length) return prev[i];
        return '';
      });
      // Pad for any extra empty rows
      while (next.length < rowCount) next.push('');
      return next;
    });
  }, [derivedValue, rowCount]);

  // Find selected option objects for each code
  const selectedOptions = useMemo(
    () => codes.map(code => CPT_CODES.find(opt => opt.cpt === code) || null),
    [codes],
  );

  // Emit both onChange (bare strings) and onLineItemsChange (rich items) when codes change
  const emitChange = useCallback(
    (newCodes: string[]) => {
      const cleaned = cleanCptCodes(newCodes);
      onChange(cleaned);
      if (onLineItemsChange) {
        onLineItemsChange(buildLineItems(cleaned));
      }
    },
    [onChange, onLineItemsChange, buildLineItems],
  );

  /**
   * The one place a code is written. Blur, Enter and option selection all
   * come through here, so they cannot disagree about what is committable
   * (GDN-3084: blur used to emit the raw input with no validation at all
   * while Enter ran `normalizeCptCode`).
   *
   * `normalizeCptCode` alone is not enough of a gate — it accepts any five
   * alphanumerics, so `psych` normalizes to `PSYCH` and billed as a
   * procedure code. Returns the committed code, or null when the input is
   * not a shape that can bill and nothing was written.
   */
  const commitCode = useCallback(
    (index: number, raw: string): string | null => {
      const normalized = normalizeCptCode(raw);
      if (!normalized || !isBillableCodeShape(normalized)) return null;
      if (normalized !== codes[index]) {
        const newCodes = [...codes];
        newCodes[index] = normalized;
        emitChange(newCodes);
      }
      return normalized;
    },
    [codes, emitChange],
  );

  /** Empty a slot — its clear control used, or its text deleted by hand. */
  const clearCode = useCallback(
    (index: number) => {
      if (!codes[index]) return;
      const newCodes = [...codes];
      newCodes[index] = '';
      emitChange(newCodes);
    },
    [codes, emitChange],
  );

  // Generic handler for when a code is selected/changed at a given index
  const handleCodeChange = useCallback(
    (index: number) => (_event: React.SyntheticEvent, newValue: CPTCodeOption | string | null) => {
      if (newValue === null) {
        clearCode(index);
        return;
      }
      // Bundled options always carry a real code; the freeSolo string path
      // is gated exactly as blur and Enter are.
      commitCode(index, typeof newValue === 'string' ? newValue : newValue.cpt);
    },
    [clearCode, commitCode],
  );

  // Ref to the index of a newly added row that should receive focus
  const pendingFocusRef = useRef<number | null>(null);

  // Add a new empty row
  const handleAddRow = useCallback(() => {
    rowIdsRef.current = [...rowIdsRef.current, crypto.randomUUID()];
    setRowCount(prev => {
      pendingFocusRef.current = prev; // prev is the index of the new row
      return prev + 1;
    });
  }, []);

  // Remove a row at a given index
  const handleRemoveRow = useCallback(
    (index: number) => {
      // Adjust expanded slot
      if (expandedSlot === index) {
        setExpandedSlot(null);
      } else if (expandedSlot !== null && expandedSlot > index) {
        setExpandedSlot(expandedSlot - 1);
      }

      // Remove the stable row ID
      rowIdsRef.current = rowIdsRef.current.filter((_, i) => i !== index);

      // Remove the local input value for this row
      setInputValues(prev => prev.filter((_, i) => i !== index));

      const newCodes = codes.filter((_, i) => i !== index);
      setRowCount(prev => Math.max(1, prev - 1));
      emitChange(newCodes);
    },
    [codes, expandedSlot, emitChange],
  );

  const handleKeyDown = useCallback(
    (index: number) => (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      const target = e.target as HTMLInputElement;
      if (target.getAttribute('aria-expanded') === 'true') return;

      const typed = (inputValues[index] ?? '').trim();
      if (typed) {
        // Commit the typed custom code. Text that cannot bill commits
        // nothing and is left in the field to be corrected, rather than
        // being written out as a procedure code.
        e.preventDefault();
        const committed = commitCode(index, typed);
        if (committed) setInputAtIndex(index, committed);
      } else if (onEnter) {
        e.preventDefault();
        onEnter();
      }
    },
    [inputValues, commitCode, setInputAtIndex, onEnter],
  );

  // Custom option rendering
  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
    option: CPTCodeOption,
  ) => {
    const { key, ...otherProps } = props;

    if (option.__custom) {
      return (
        <li key={key} {...otherProps}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontStyle: 'italic',
              color: 'primary.main',
            }}
          >
            <Add sx={{ fontSize: '1rem' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Add custom code: {option.cpt}
            </Typography>
          </Box>
        </li>
      );
    }

    return (
      <li key={key} {...otherProps}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {option.cpt}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {option.description}
          </Typography>
        </Box>
      </li>
    );
  };

  // Convert a response line item to a request line item, preserving all metadata
  const toRequestLineItem = useCallback(
    (li: CPTCodePickerLineItem): WorkingClaimLineItem => ({
      code_id: li.code_id ?? '',
      source: li.source ?? 'provider_selected',
      modifiers: li.modifiers,
      modifier_sources: li.modifier_sources,
      units: li.units ?? undefined,
      charge_cents: li.charge_cents ?? undefined,
      diagnosis_codes: li.diagnosis_codes,
    }),
    [],
  );

  // Handle modifier changes for a specific code slot
  const handleModifiersChange = useCallback(
    (codeIndex: number, newModifiers: string[]) => {
      if (!lineItems || !onLineItemsChange) return;

      const updated: WorkingClaimLineItem[] = lineItems.map((li, idx) => {
        if (idx !== codeIndex) return toRequestLineItem(li);

        const existingMods = new Set(li.modifiers ?? []);
        const newModSources: Record<string, BillingCodeSource> = {};

        for (const mod of newModifiers) {
          if (existingMods.has(mod) && li.modifier_sources?.[mod]) {
            newModSources[mod] = li.modifier_sources[mod];
          } else {
            newModSources[mod] = 'provider_selected';
          }
        }

        return {
          ...toRequestLineItem(li),
          modifiers: newModifiers,
          modifier_sources: newModSources,
        };
      });

      onLineItemsChange(updated);
    },
    [lineItems, onLineItemsChange, toRequestLineItem],
  );

  // Handle units changes for a specific code slot
  const handleUnitsChange = useCallback(
    (codeIndex: number, newUnits: number) => {
      if (!lineItems || !onLineItemsChange) return;

      const clamped = Math.max(1, Math.min(99, newUnits));

      const updated: WorkingClaimLineItem[] = lineItems.map((li, idx) => {
        const base = toRequestLineItem(li);
        if (idx !== codeIndex) return base;
        return { ...base, units: clamped };
      });

      onLineItemsChange(updated);
    },
    [lineItems, onLineItemsChange, toRequestLineItem],
  );

  // Handle diagnosis code pointer changes for a specific code slot
  const handleDiagnosisCodesChange = useCallback(
    (codeIndex: number, newDiagnosisCodes: string[]) => {
      if (!lineItems || !onLineItemsChange) return;

      const updated: WorkingClaimLineItem[] = lineItems.map((li, idx) => {
        const base = toRequestLineItem(li);
        if (idx !== codeIndex) return base;
        return { ...base, diagnosis_codes: newDiagnosisCodes };
      });

      onLineItemsChange(updated);
    },
    [lineItems, onLineItemsChange, toRequestLineItem],
  );

  // Render the toggle link for details under each code slot
  const renderModifiersToggle = useCallback(
    (index: number) => {
      if (!showModifiers || !codes[index]) return null;

      const isExpanded = expandedSlot === index;
      const currentModifiers = lineItems?.[index]?.modifiers ?? [];
      const currentUnits = lineItems?.[index]?.units ?? 1;

      const hasMods = currentModifiers.length > 0;
      const hasNonDefaultUnits = currentUnits !== 1;
      const isAiRecommended = lineItems?.[index]?.source === 'ai_recommended';
      return (
        <Box sx={{ mt: 0.5 }}>
          <Typography
            component="button"
            variant="caption"
            onClick={() => toggleSlotExpanded(index)}
            sx={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: isExpanded ? 'primary.main' : 'text.secondary',
              fontSize: '0.7rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { color: 'text.primary' },
            }}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for code ${codes[index]}`}
          >
            {isExpanded ? '\u25BE' : '\u25B8'} Details
            {isAiRecommended && (
              <Tooltip title="JotPsych suggested" placement="top" arrow>
                <AutoAwesome sx={{ fontSize: '0.7rem', color: theme.palette.secondary.main }} />
              </Tooltip>
            )}
            {!isExpanded && (hasMods || hasNonDefaultUnits) && (
              <Chip
                label={[
                  hasMods ? currentModifiers.join(', ') : null,
                  hasNonDefaultUnits ? `${currentUnits}u` : null,
                ]
                  .filter(Boolean)
                  .join(' \u00B7 ')}
                size="small"
                sx={{
                  height: 16,
                  fontSize: '0.6rem',
                  fontFamily: 'monospace',
                  ml: 0.5,
                }}
              />
            )}
          </Typography>
        </Box>
      );
    },
    [showModifiers, codes, expandedSlot, toggleSlotExpanded, lineItems, theme],
  );

  // Render the expanded content (modifiers, units, diagnosis linking) for a specific slot
  const renderExpandedDetails = useCallback(
    (index: number) => {
      if (expandedSlot !== index || !showModifiers || !codes[index]) return null;

      const currentModifiers = lineItems?.[index]?.modifiers ?? [];
      const currentUnits = lineItems?.[index]?.units ?? 1;
      const codeLabel = codes[index];

      return (
        <Collapse in>
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            {/* Code label chip */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                label={codeLabel}
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Modifiers & Units
              </Typography>
            </Box>

            <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
              {/* Modifier Autocomplete */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Autocomplete
                  multiple
                  freeSolo
                  size="small"
                  value={currentModifiers}
                  options={MODIFIER_CODES}
                  onChange={(_e, newValue) => handleModifiersChange(index, newValue)}
                  renderValue={(tagValue, getTagProps) =>
                    tagValue.map((mod, i) => {
                      const { key, ...tagProps } = getTagProps({ index: i });
                      const modSource = lineItems?.[index]?.modifier_sources?.[mod];
                      return (
                        <Chip
                          key={key}
                          {...tagProps}
                          label={mod}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            fontFamily: 'monospace',
                            fontWeight: 500,
                            borderLeft: modSource
                              ? `2px solid ${
                                  modSource === 'ai_recommended'
                                    ? theme.palette.secondary.main
                                    : theme.palette.primary.main
                                }`
                              : undefined,
                          }}
                        />
                      );
                    })
                  }
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props as React.HTMLAttributes<HTMLLIElement> & {
                      key?: React.Key;
                    };
                    const modOption = COMMON_MODIFIERS.find(m => m.code === option);
                    return (
                      <li key={key} {...otherProps}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {option}
                          </Typography>
                          {modOption && (
                            <Typography variant="caption" color="text.secondary">
                              {modOption.description}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder="Modifiers"
                      variant="standard"
                      sx={{
                        '& .MuiInput-underline:before': { borderBottomStyle: 'dashed' },
                      }}
                      slotProps={{
                        ...params.slotProps,
                        htmlInput: {
                          ...params.slotProps.htmlInput,
                          maxLength: 2,
                        },
                      }}
                    />
                  )}
                  disabled={disabled}
                />
              </Box>

              {/* Units stepper */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pt: 0.5, flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  Units
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleUnitsChange(index, currentUnits - 1)}
                  disabled={disabled || currentUnits <= 1}
                  aria-label="Decrease units"
                  sx={{ p: 0.25 }}
                >
                  <Remove sx={{ fontSize: '0.875rem' }} />
                </IconButton>
                <TextField
                  value={currentUnits}
                  onChange={e => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) handleUnitsChange(index, val);
                  }}
                  variant="standard"
                  size="small"
                  disabled={disabled}
                  sx={{
                    '& .MuiInput-underline:before': { borderBottomStyle: 'dashed' },
                  }}
                  slotProps={{
                    htmlInput: {
                      min: 1,
                      max: 99,
                      style: {
                        textAlign: 'center',
                        width: 28,
                        fontSize: '0.8rem',
                        fontFamily: 'monospace',
                      },
                      'aria-label': `Units for code ${codes[index]}`,
                    },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleUnitsChange(index, currentUnits + 1)}
                  disabled={disabled || currentUnits >= 99}
                  aria-label="Increase units"
                  sx={{ p: 0.25 }}
                >
                  <Add sx={{ fontSize: '0.875rem' }} />
                </IconButton>
              </Box>
            </Stack>

            {/* Diagnosis code pointers */}
            {claimDiagnosisCodes.length > 0 && (
              <Box sx={{ mt: 1.5, pt: 1, borderTop: `1px dashed ${theme.palette.divider}` }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5, display: 'block' }}
                >
                  Linked diagnoses
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {(() => {
                    const lineDx = lineItems?.[index]?.diagnosis_codes ?? [];
                    const allApply = lineDx.length === 0;
                    return claimDiagnosisCodes.map(dx => {
                      const isLinked = allApply || lineDx.includes(dx);
                      return (
                        <Chip
                          key={dx}
                          label={dx}
                          size="small"
                          variant={isLinked ? 'filled' : 'outlined'}
                          color={isLinked ? 'primary' : 'default'}
                          onClick={() => {
                            if (disabled) return;
                            if (allApply) {
                              handleDiagnosisCodesChange(
                                index,
                                claimDiagnosisCodes.filter(d => d !== dx),
                              );
                            } else if (isLinked) {
                              const remaining = lineDx.filter(d => d !== dx);
                              handleDiagnosisCodesChange(index, remaining);
                            } else {
                              handleDiagnosisCodesChange(index, [...lineDx, dx]);
                            }
                          }}
                          disabled={disabled}
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            height: 22,
                            cursor: disabled ? 'default' : 'pointer',
                          }}
                        />
                      );
                    });
                  })()}
                </Box>
                {(() => {
                  const lineDx = lineItems?.[index]?.diagnosis_codes ?? [];
                  return lineDx.length === 0 ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: 'block', fontSize: '0.65rem', fontStyle: 'italic' }}
                    >
                      All diagnoses apply. Click one to link specific codes.
                    </Typography>
                  ) : null;
                })()}
              </Box>
            )}
          </Box>
        </Collapse>
      );
    },
    [
      expandedSlot,
      showModifiers,
      codes,
      lineItems,
      handleModifiersChange,
      handleUnitsChange,
      handleDiagnosisCodesChange,
      claimDiagnosisCodes,
      disabled,
      theme,
    ],
  );

  // Filter options based on input, appending a synthetic "Add custom code" row when no exact match
  const filterOptions = (options: CPTCodeOption[], state: { inputValue: string }) => {
    const rawInput = state.inputValue.trim();
    const input = rawInput.toLowerCase();
    const normalizedInput = normalizeCptCode(rawInput);
    if (!input) return options;

    const filtered = options.filter(
      opt =>
        opt.cpt.toLowerCase().includes(input) ||
        opt.description.toLowerCase().includes(input) ||
        opt.category.toLowerCase().includes(input),
    );

    // Offer "Add custom code" only for an off-list code that could actually
    // bill. Offering it for a description fragment is what made typing one
    // and clicking away look like a way to enter a code.
    const hasExactMatch = options.some(opt => opt.cpt === normalizedInput);
    if (!hasExactMatch && normalizedInput && isBillableCodeShape(normalizedInput)) {
      filtered.push({
        cpt: normalizedInput,
        description: 'Add custom code',
        category: 'Other',
        __custom: true,
      });
    }

    return filtered;
  };

  return (
    <Box>
      {showHeader && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Enter CPT codes for this encounter (optional).
          </Typography>
          <Tooltip title={CPT_INFO_TOOLTIP} placement="top" arrow>
            <InfoOutlined
              sx={{
                fontSize: '1rem',
                color: 'text.secondary',
                cursor: 'help',
              }}
            />
          </Tooltip>
        </Box>
      )}

      {showHelperText && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1.5 }}>
          This information will help JotPsych audit your note to ensure reimbursement.
        </Typography>
      )}

      <Stack spacing={1}>
        {codes.map((code, index) => (
          <Box key={rowIdsRef.current[index] ?? index}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <Autocomplete
                value={selectedOptions[index]}
                inputValue={inputValues[index] ?? ''}
                onInputChange={(_event, newInputValue, reason) => {
                  if (reason === 'clear') {
                    setInputAtIndex(index, '');
                    clearCode(index);
                    return;
                  }
                  // Hold what was typed verbatim. Upcasing, stripping and
                  // clipping to five characters on every keystroke meant a
                  // description search only ever matched on the first five
                  // characters typed. Normalisation and the billable-shape
                  // check belong at commit, not mid-word.
                  setInputAtIndex(index, newInputValue);
                }}
                onChange={handleCodeChange(index)}
                onBlur={() => {
                  const typed = (inputValues[index] ?? '').trim();
                  if (!typed) {
                    clearCode(index);
                    return;
                  }
                  // Abandoned search text is not a code: snap the field back
                  // to whatever is actually committed rather than writing the
                  // text out as one.
                  setInputAtIndex(index, commitCode(index, typed) ?? codes[index] ?? '');
                }}
                options={CPT_CODES}
                groupBy={option => option.category}
                getOptionLabel={option => (typeof option === 'string' ? option : option.cpt)}
                renderOption={renderOption}
                filterOptions={filterOptions}
                freeSolo
                fullWidth
                size={size}
                disabled={disabled}
                id={inputId ? (index === 0 ? inputId : `${inputId}-${index}`) : undefined}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={inputLabel ?? undefined}
                    placeholder="e.g., 99213"
                    onKeyDown={handleKeyDown(index)}
                    sx={textFieldSx}
                    slotProps={{
                      ...params.slotProps,
                      htmlInput: {
                        ...params.slotProps.htmlInput,
                        ref: (el: HTMLInputElement | null) => {
                          setRefValue(params.slotProps.htmlInput.ref, el);
                          // Auto-focus newly added rows
                          if (pendingFocusRef.current === index && el) {
                            requestAnimationFrame(() => el.focus());
                            pendingFocusRef.current = null;
                          }
                        },
                      },
                    }}
                  />
                )}
                slotProps={{ popper: { sx: popperSx }, listbox: { sx: { maxHeight: 300 } } }}
                sx={{ flex: 1 }}
              />
              {/* Remove button: show when there's more than 1 row, or the row has a code.
                  A single-code field has nothing to remove down to — the
                  Autocomplete's own clear control already empties it. */}
              {!isSingleSelect && (codes.length > 1 || code) && (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveRow(index)}
                  disabled={disabled}
                  aria-label={`Remove CPT code ${code || index + 1}`}
                  sx={{ mt: size === 'small' ? 0.75 : 1.25 }}
                >
                  <Close sx={{ fontSize: '1rem' }} />
                </IconButton>
              )}
            </Box>
            {renderModifiersToggle(index)}
            {renderExpandedDetails(index)}
          </Box>
        ))}

        {/* Add code button — disabled when last row is still empty, and
            absent entirely once the row cap is reached (a capped field has
            nowhere to put another code). */}
        {codes.length < rowCap && (
          <Button
            size="small"
            startIcon={<Add sx={{ fontSize: '1rem' }} />}
            onClick={handleAddRow}
            disabled={disabled || !codes[codes.length - 1]}
            sx={{
              alignSelf: 'flex-start',
              textTransform: 'none',
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.8rem',
              py: 0.25,
              '&:hover': { color: 'text.primary' },
            }}
          >
            Add code
          </Button>
        )}
      </Stack>

      {/* Use Suggested Codes Button */}
      {suggestedCodes &&
        suggestedCodes.length > 0 &&
        suggestedCodes.some(c => c && c.trim() !== '') &&
        onUseSuggestedCodes &&
        (!value || value.length === 0 || value.every(c => !c || c.trim() === '')) && (
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                JotPsych suggests these codes based on your note content:
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              {suggestedCodes
                .filter(c => c && c.trim() !== '')
                .map((code, idx) => (
                  <Chip
                    key={idx}
                    label={code}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      opacity: 0.7,
                      borderStyle: 'dashed',
                      borderColor: theme.palette.text.disabled,
                      color: theme.palette.text.secondary,
                    }}
                  />
                ))}
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={onUseSuggestedCodes}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              Use Suggested Codes
            </Button>
          </Box>
        )}
    </Box>
  );
};
